import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Box, Container, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { ArrowBack, Info } from '@mui/icons-material';
import { useTaxBinder } from '@/hooks/api/useTaxBinders';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';

export const Route = createFileRoute('/tax-binders/$binderId/efile-authorization')({
  component: EfileAuthorizationSigningPage,
  errorComponent: QueryErrorBoundary,
});

/**
 * E-file Authorization Signing Page (Form 8879)
 *
 * NOTE: This route is deprecated. E-file authorization signing is now handled
 * via the Tax Returns system. Clients will receive signature requests via email
 * when their preparer sends a Form 8879 signature request from the Tax Returns tab.
 *
 * This page now redirects users back to their binder with an informational message.
 */
function EfileAuthorizationSigningPage() {
  const { binderId } = Route.useParams();
  const navigate = useNavigate();
  const { data: binder, isLoading: isLoadingBinder } = useTaxBinder(binderId);

  const handleBack = () => {
    navigate({
      to: '/tax-binders/$binderId',
      params: { binderId },
    });
  };

  // Loading state
  if (isLoadingBinder) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  // E-file authorization is now handled via Tax Returns system
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Info sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          E-file Authorization
        </Typography>

        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="body1" paragraph>
            E-file authorization signing is now handled via our new Tax Returns system.
          </Typography>
          <Typography variant="body2">
            When your preparer is ready for you to sign Form 8879 (E-file Authorization), you will receive an email with
            a link to sign electronically.
          </Typography>
        </Alert>

        <Typography variant="body1" color="text.secondary" paragraph>
          {binder?.name
            ? `Return to your "${binder.name}" tax binder to view your tax return status.`
            : 'Return to your tax binder to view your tax return status.'}
        </Typography>

        <Button variant="contained" size="large" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
          Return to Binder
        </Button>
      </Paper>
    </Container>
  );
}
